import useSWR from 'swr';
import { articlesService, isApiError, type PublicArticleRequestResult } from '@dopomogai/api-client';
import { useTranslation } from 'react-i18next';
import type { PublicArticleListQuery, ArticleSummary, PublicArticle } from '@dopomogai/types';
import { AgentKitError } from '../../types';

type UseArticlesListArg = string | PublicArticleListQuery | undefined;

const resolveListQuery = (input: UseArticlesListArg, locale: string): PublicArticleListQuery => {
  if (typeof input === 'string') {
    return { locale, category: input };
  }

  return {
    locale,
    ...(input || {}),
  };
};

const listFetcher = async ([, query]: [string, PublicArticleListQuery]) => {
  const response = await articlesService.getList(query);
  if (isApiError(response)) {
    throw new AgentKitError('Failed to fetch articles list', response);
  }
  return response;
};

export const useArticlesList = (query?: UseArticlesListArg) => {
  const { i18n } = useTranslation();
  const resolvedQuery = resolveListQuery(query, i18n.language);

  const { data, error, isLoading, mutate } = useSWR<ArticleSummary[], AgentKitError>(
    ['public-articles-list', resolvedQuery],
    listFetcher,
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    articles: data || [],
    isLoading,
    error,
    mutate,
  };
};

const detailFetcher = async ([, slug, locale]: [string, string, string]) => {
  const response = await articlesService.getBySlug(slug, locale);
  if (isApiError(response)) {
    throw new AgentKitError(`Failed to fetch article: ${slug}`, response);
  }
  return response;
};

const routeFetcher = async ([, path, locale]: [string, string, string]) => {
  const response = await articlesService.getByRoute(path, locale);
  if (isApiError(response)) {
    throw new AgentKitError(`Failed to fetch article by route: ${path}`, response);
  }
  return response;
};

const previewFetcher = async ([, token]: [string, string]) => {
  const response = await articlesService.getPreview(token);
  if (isApiError(response)) {
    throw new AgentKitError('Failed to fetch article preview', response);
  }
  return response;
};

const normalizeDetailResult = (result?: PublicArticleRequestResult) => ({
  article: result?.article as PublicArticle | undefined,
  redirectPath: result?.redirectPath || undefined,
  responseUrl: result?.responseUrl || undefined,
});

export const useArticle = (slug?: string) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const { data, error, isLoading, mutate } = useSWR<PublicArticleRequestResult, AgentKitError>(
    slug ? ['public-article-detail', slug, locale] : null,
    detailFetcher,
    {
      revalidateOnFocus: false,
    },
  );

  return {
    ...normalizeDetailResult(data),
    isLoading,
    error,
    mutate,
  };
};

export const useArticleByRoute = (path?: string) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const { data, error, isLoading, mutate } = useSWR<PublicArticleRequestResult, AgentKitError>(
    path ? ['public-article-by-route', path, locale] : null,
    routeFetcher,
    {
      revalidateOnFocus: false,
    },
  );

  return {
    ...normalizeDetailResult(data),
    isLoading,
    error,
    mutate,
  };
};

export const useArticlePreview = (token?: string) => {
  const { data, error, isLoading, mutate } = useSWR<PublicArticleRequestResult, AgentKitError>(
    token ? ['public-article-preview', token] : null,
    previewFetcher,
    {
      revalidateOnFocus: false,
    },
  );

  return {
    ...normalizeDetailResult(data),
    isLoading,
    error,
    mutate,
  };
};
